import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const SESSION_STATE_PREFIX = 'nexus:session-state:';

const getSessionStorageKey = (key: string | null | undefined) => {
  if (!key) return null;
  return `${SESSION_STATE_PREFIX}${key}`;
};

const readSessionValue = <T,>(storageKey: string | null, defaultValue: T): T => {
  if (!storageKey || typeof window === 'undefined') return defaultValue;

  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (raw === null) return defaultValue;
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn('Failed to read session state:', error);
    window.sessionStorage.removeItem(storageKey);
    return defaultValue;
  }
};

const writeSessionValue = <T,>(storageKey: string | null, value: T) => {
  if (!storageKey || typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to write session state:', error);
  }
};

const removeSessionValue = (storageKey: string | null) => {
  if (!storageKey || typeof window === 'undefined') return;
  window.sessionStorage.removeItem(storageKey);
};

export const clearSessionStateKeys = (scope?: string) => {
  if (typeof window === 'undefined') return;

  const prefix = scope ? `${SESSION_STATE_PREFIX}${scope}` : SESSION_STATE_PREFIX;
  for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = window.sessionStorage.key(index);
    if (key?.startsWith(prefix)) {
      window.sessionStorage.removeItem(key);
    }
  }
};

export function useSessionState<T>(
  key: string | null | undefined,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storageKey = useMemo(() => getSessionStorageKey(key), [key]);
  const defaultValueRef = useRef(defaultValue);
  defaultValueRef.current = defaultValue;

  const [value, setValue] = useState<T>(() => readSessionValue(storageKey, defaultValue));

  useEffect(() => {
    setValue(readSessionValue(storageKey, defaultValueRef.current));
  }, [storageKey]);

  const setSessionValue: Dispatch<SetStateAction<T>> = useCallback((nextValue) => {
    setValue((currentValue) => {
      const resolvedValue =
        typeof nextValue === 'function'
          ? (nextValue as (current: T) => T)(currentValue)
          : nextValue;
      writeSessionValue(storageKey, resolvedValue);
      return resolvedValue;
    });
  }, [storageKey]);

  const clearSessionValue = useCallback(() => {
    removeSessionValue(storageKey);
    setValue(defaultValueRef.current);
  }, [storageKey]);

  return [value, setSessionValue, clearSessionValue];
}
